// Open / new / save / recent files / drag-drop / unload guard.
// Save uses the File System Access API when available so Ctrl+S writes back to the same file.
import { openLabel, newLabel, saveLabel } from '../itf/label.js';
import { encodeItf, decodeItf } from '../itf/encoding.js';
import { printerFileNames, buildJobBytes, parseJobBytes, referencedAssets, deviceInstance, withDeviceUri } from '../itf/job.js';
import { toMachineText } from '../itf/machine.js';
import { checkNow } from './check.js';
import { S, emit, loadLabel, markSaved, isDirty } from './state.js';
import { $, h, toast, confirmDialog, ago } from './dom.js';
import { icon } from './icons.js';
import { ensureBwip } from './bwip.js';

const DB_NAME = 'labels-editor';
const STORE = 'recent';
const RECENT_MAX = 10;
const ITF_TYPES = [{ description: 'CoLOS label (.itf, .image)', accept: { 'application/octet-stream': ['.itf', '.image'] } }];
const PRINTER_KEY = 'labels.printerInstances';
/** Printer ids seen in opened machine files, with counts; exports use the most common one. */
const rememberedInstance = () => {
  try {
    const m = JSON.parse(localStorage.getItem(PRINTER_KEY) || '{}');
    return Object.entries(m).sort((x, y) => y[1] - x[1])[0]?.[0] || null;
  } catch {
    return null;
  }
};
const rememberInstance = (id) => {
  try {
    const m = JSON.parse(localStorage.getItem(PRINTER_KEY) || '{}');
    m[id] = (m[id] || 0) + 1;
    localStorage.setItem(PRINTER_KEY, JSON.stringify(m));
  } catch {
    /* best effort */
  }
};

/** Tests (and old browsers) can force the <input type=file> / download fallbacks. */
const fsDisabled = () => window.__LABELS_NO_FS_ACCESS__ === true || new URLSearchParams(location.search).has('nofs');
const hasOpenPicker = () => !fsDisabled() && typeof window.showOpenFilePicker === 'function';
const hasSavePicker = () => !fsDisabled() && typeof window.showSaveFilePicker === 'function';

async function confirmDiscard() {
  if (!isDirty()) return true;
  return confirmDialog({
    title: 'Discard unsaved changes?',
    body: `“${S.filename}” has changes that have not been saved.`,
    okLabel: 'Discard changes',
    danger: true,
  });
}

// ----- open --------------------------------------------------------------------
export async function openDialog() {
  if (!(await confirmDiscard())) return;
  if (hasOpenPicker()) {
    try {
      const [handle] = await window.showOpenFilePicker({ types: ITF_TYPES, multiple: false });
      await openFile(await handle.getFile(), handle, { skipConfirm: true });
      return;
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      console.warn('showOpenFilePicker failed, using file input', err);
    }
  }
  const input = $('#file-input');
  input.value = '';
  input.click();
}

export async function openFile(file, handle = null, { skipConfirm = false } = {}) {
  if (!skipConfirm && !(await confirmDiscard())) return false;
  let bytes;
  try {
    bytes = new Uint8Array(await file.arrayBuffer());
  } catch (err) {
    toast(`Could not read ${file.name}: ${err.message}`, 'bad');
    return false;
  }
  if (/\.job$/i.test(file.name)) {
    const image = parseJobBytes(bytes);
    toast(
      image
        ? `${file.name} is only a pointer to “${image}”. Open that .image file instead.`
        : `${file.name} is not a printer job file.`,
      'warn',
    );
    return false;
  }
  return openBytes(bytes, file.name, handle);
}

function openBytes(bytes, name, handle = null) {
  let label;
  try {
    label = openLabel(bytes);
  } catch (err) {
    console.error(err);
    toast(`Can't open ${name}: ${err.message || err}`, 'bad');
    return false;
  }
  loadLabel(label, { filename: name, handle });
  const inst = deviceInstance(label.device.uri);
  if (inst && /\.image$/i.test(name)) rememberInstance(inst); // exports for this printer reuse its id
  S.pendingFit = true;
  if (S.fields.some((f) => f.kind === 'barcode')) ensureBwip().catch((e) => toast(e.message, 'warn'));
  saveRecent(name, bytes);
  toast(`Opened ${name}`, 'good');
  return true;
}

export async function newBlank() {
  if (!(await confirmDiscard())) return;
  loadLabel(newLabel(), { filename: 'untitled.itf', handle: null, saved: false });
  S.pendingFit = true;
  toast('New blank label · 128 × 71 mm', 'good');
}

// ----- save --------------------------------------------------------------------
function downloadBytes(bytes, name) {
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/octet-stream' }));
  const a = h('a', { href: url, download: name, style: { display: 'none' } });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

async function confirmErrors() {
  const issues = checkNow();
  const errors = issues.filter((i) => i.severity === 'error');
  if (!errors.length) return true;
  const byKey = new Map(S.fields.map((f) => [f.key, f]));
  const list = h(
    'ul',
    { class: 'check-list', style: { margin: '12px 0 0', maxHeight: '40vh', overflow: 'auto' } },
    errors.map((i) =>
      h(
        'li',
        { class: 'check-item error' },
        h('span', { class: 'check-icon' }, icon('error', '')),
        h('span', { class: 'check-msg' }, i.message),
        h('span', { class: 'check-field' }, byKey.get(i.fieldKey)?.name || ''),
      ),
    ),
  );
  const body = h(
    'div',
    null,
    h('div', { class: 'dpi-sub', style: { marginBottom: '0' } }, `The printer check found ${errors.length} error${errors.length === 1 ? '' : 's'}. The label may not print as expected.`),
    list,
  );
  return confirmDialog({ title: 'Save with errors?', body, okLabel: 'Save anyway', cancelLabel: 'Cancel', danger: true });
}

/**
 * Save the label. mode: 'save' (same file when possible), 'as' (always pick), 'download' (always download).
 * Resolves true when bytes were written or downloaded.
 */
export async function save(mode = 'save') {
  if (!S.label) return false;
  if (!(await confirmErrors())) return false;
  let bytes;
  try {
    bytes = saveLabel(S.label);
  } catch (err) {
    console.error(err);
    toast(`Save failed: ${err.message || err}`, 'bad');
    return false;
  }
  let name = S.filename || 'untitled.itf';
  let handle = S.fileHandle;
  const wantPicker = mode !== 'download' && (hasSavePicker() || (handle && handle.createWritable));

  if (wantPicker) {
    try {
      if (!handle || mode === 'as' || !handle.createWritable) {
        if (!hasSavePicker()) throw new Error('Save picker unavailable');
        handle = await window.showSaveFilePicker({ suggestedName: name, types: ITF_TYPES });
      }
      if (handle.queryPermission && (await handle.queryPermission({ mode: 'readwrite' })) !== 'granted') {
        if ((await handle.requestPermission({ mode: 'readwrite' })) !== 'granted') throw new Error('Permission to write the file was denied');
      }
      const w = await handle.createWritable();
      await w.write(bytes);
      await w.close();
      name = handle.name || name;
    } catch (err) {
      if (err && err.name === 'AbortError') return false;
      console.warn(err);
      toast(`Couldn't write the file directly (${err.message}) — downloading instead`, 'warn');
      handle = null;
      downloadBytes(bytes, name);
    }
  } else {
    downloadBytes(bytes, name);
    if (mode === 'download') {
      toast(`Downloaded ${name}`, 'good');
      if (!S.fileHandle) markSaved({ filename: name });
      saveRecent(name, bytes);
      return true;
    }
  }
  markSaved({ filename: name, handle });
  saveRecent(name, bytes);
  toast(`Saved ${name}`, 'good');
  return true;
}

// ----- printer export (.image + .job) -----------------------------------------------
/** The two files the machine loads. The printer id of a machine file we opened earlier is reused. */
export function buildPrinterFiles() {
  const names = printerFileNames(S.filename);
  let text = decodeItf(saveLabel(S.label)).text;
  const generic = !deviceInstance(S.label.device.uri);
  const inst = generic ? rememberedInstance() : deviceInstance(S.label.device.uri);
  if (generic && inst) text = withDeviceUri(text, `NGEDriver[${S.label.device.uri.replace(/^NGEDriver\[|\]\/?$/g, '')}.${inst}]/`);
  const machine = toMachineText(text, { deviceInstance: inst });
  text = machine.text;
  return { names, assets: referencedAssets(text), usedPrinterId: generic && !!inst, warnings: machine.warnings, image: encodeItf(text), job: buildJobBytes(names.image) };
}

export async function exportForPrinter() {
  if (!S.label) return false;
  if (!(await confirmErrors())) return false;
  let files;
  try {
    files = buildPrinterFiles();
  } catch (err) {
    console.error(err);
    toast(`Export failed: ${err.message || err}`, 'bad');
    return false;
  }
  const { names, assets } = files;
  const line = (t) => h('div', { class: 'dpi-sub', style: { marginBottom: '6px' } }, t);
  const body = h(
    'div',
    null,
    line(`Two files will be created: ${names.image} (the label) and ${names.job} (tells the printer which label to load). Keep them together in the same folder, with these exact names.`),
    assets.logos.length
      ? line(`Not included: logo file${assets.logos.length > 1 ? 's' : ''} ${assets.logos.join(', ')}. ${assets.logos.length > 1 ? 'They' : 'It'} must already be on the printer, or the printer reports “Source file not found”.`)
      : null,
    ...files.warnings.map(line),
    hasDirPicker() ? line('Choose the USB stick (or a folder) next.') : line('Your browser will download both files. Copy them to the USB stick together.'),
  );
  if (!(await confirmDialog({ title: 'Export for printer', body, okLabel: 'Export' }))) return false;

  if (hasDirPicker()) {
    try {
      const dir = await window.showDirectoryPicker({ mode: 'readwrite' });
      for (const [name, bytes] of [[names.image, files.image], [names.job, files.job]]) {
        const w = await (await dir.getFileHandle(name, { create: true })).createWritable();
        await w.write(bytes);
        await w.close();
      }
      toast(`Wrote ${names.image} and ${names.job} to “${dir.name}”`, 'good');
      return true;
    } catch (err) {
      if (err && err.name === 'AbortError') return false;
      console.warn(err);
      toast(`Couldn't write to the folder (${err.message}) — downloading instead`, 'warn');
    }
  }
  downloadBytes(files.image, names.image);
  setTimeout(() => downloadBytes(files.job, names.job), 400);
  toast(`Downloaded ${names.image} and ${names.job} — copy both to the printer`, 'good');
  return true;
}

const hasDirPicker = () => !fsDisabled() && typeof window.showDirectoryPicker === 'function';

// ----- recent files (IndexedDB) ---------------------------------------------------
let dbPromise = null;
function db() {
  if (!('indexedDB' in window)) return Promise.reject(new Error('no IndexedDB'));
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'name' });
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return dbPromise;
}
const reqP = (req) => new Promise((res, rej) => ((req.onsuccess = () => res(req.result)), (req.onerror = () => rej(req.error))));

async function saveRecent(name, bytes) {
  try {
    const d = await db();
    await reqP(d.transaction(STORE, 'readwrite').objectStore(STORE).put({ name, bytes: bytes.slice().buffer, ts: Date.now() }));
    const items = await listRecent();
    if (items.length > RECENT_MAX) {
      const st = d.transaction(STORE, 'readwrite').objectStore(STORE);
      for (const it of items.slice(RECENT_MAX)) st.delete(it.name);
    }
    renderWelcomeRecent();
  } catch {
    /* recent list is best-effort */
  }
}

export async function listRecent() {
  try {
    const d = await db();
    const all = await reqP(d.transaction(STORE, 'readonly').objectStore(STORE).getAll());
    return all.sort((a, b) => b.ts - a.ts);
  } catch {
    return [];
  }
}

export async function openRecent(name) {
  if (!(await confirmDiscard())) return;
  try {
    const d = await db();
    const it = await reqP(d.transaction(STORE, 'readonly').objectStore(STORE).get(name));
    if (!it) {
      toast(`${name} is no longer in recent files`, 'warn');
      return;
    }
    openBytes(new Uint8Array(it.bytes), it.name, null);
  } catch (err) {
    toast(`Reopen failed: ${err.message}`, 'bad');
  }
}

export async function renderWelcomeRecent() {
  const root = $('#welcome-recent');
  if (!root) return;
  const items = (await listRecent()).slice(0, 6);
  if (!items.length) {
    root.replaceChildren();
    return;
  }
  root.replaceChildren(
    h('div', { class: 'welcome-recent-title' }, 'Recent'),
    ...items.map((it) =>
      h(
        'div',
        {
          class: 'welcome-recent-item',
          role: 'button',
          tabindex: '0',
          'aria-label': `Reopen ${it.name}`,
          onClick: () => openRecent(it.name),
          onKeydown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openRecent(it.name);
            }
          },
        },
        icon('new', ''),
        h('span', null, it.name),
        h('span', { class: 'when' }, ago(it.ts)),
      ),
    ),
  );
  for (const svg of root.querySelectorAll('svg')) {
    svg.setAttribute('width', '14');
    svg.setAttribute('height', '14');
  }
}

// ----- wiring --------------------------------------------------------------------
export function initIO() {
  const input = $('#file-input');
  input.addEventListener('change', () => {
    const file = input.files && input.files[0];
    if (file) openFile(file, null, { skipConfirm: true });
    input.value = '';
  });
  $('#welcome-open').onclick = openDialog;
  $('#welcome-new').onclick = newBlank;
  $('#btn-save').onclick = () => save('save');

  const nameInput = $('#filename-input');
  nameInput.addEventListener('change', () => {
    let v = nameInput.value.trim() || 'untitled.itf';
    if (!/\.(itf|image)$/i.test(v)) v += '.itf';
    if (v !== S.filename) {
      S.filename = v;
      if (S.fileHandle && S.fileHandle.name !== v) S.fileHandle = null; // renamed: next save asks where
      emit('file');
    }
    nameInput.value = v;
  });
  nameInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === 'Escape') nameInput.blur();
  });

  // Drag and drop anywhere
  let depth = 0;
  const hasFiles = (e) => e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files');
  window.addEventListener('dragenter', (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    depth++;
    $('#drop-overlay').classList.add('visible');
  });
  window.addEventListener('dragleave', (e) => {
    if (!hasFiles(e)) return;
    depth = Math.max(0, depth - 1);
    if (!depth) $('#drop-overlay').classList.remove('visible');
  });
  window.addEventListener('dragover', (e) => {
    if (hasFiles(e)) e.preventDefault();
  });
  window.addEventListener('drop', async (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    depth = 0;
    $('#drop-overlay').classList.remove('visible');
    const item = e.dataTransfer.items && e.dataTransfer.items[0];
    let handle = null;
    if (item && item.getAsFileSystemHandle && !fsDisabled()) {
      try {
        const hnd = await item.getAsFileSystemHandle();
        if (hnd && hnd.kind === 'file') handle = hnd;
      } catch {
        /* ignore */
      }
    }
    const file = e.dataTransfer.files[0];
    if (file) openFile(file, handle);
  });

  window.addEventListener('beforeunload', (e) => {
    if (isDirty()) {
      e.preventDefault();
      e.returnValue = '';
    }
  });

  renderWelcomeRecent();
}

export function renderFileChrome() {
  const dirty = isDirty();
  const nameInput = $('#filename-input');
  if (document.activeElement !== nameInput) nameInput.value = S.label ? S.filename : '';
  $('#btn-save').disabled = !S.label;
  const ind = $('#save-indicator');
  ind.classList.toggle('dirty', dirty);
  ind.title = dirty ? 'Unsaved changes' : 'Saved';
  ind.setAttribute('aria-label', ind.title);
  $('#status-dot').classList.toggle('dirty', dirty);
  const stat = $('#stat-saved');
  if (!S.label) stat.textContent = 'No file';
  else if (dirty) stat.textContent = 'Unsaved changes';
  else if (S.savedAt) {
    const sec = Math.floor((Date.now() - S.savedAt) / 1000);
    stat.textContent = sec < 5 ? 'Saved just now' : `Saved ${ago(S.savedAt)}${sec < 60 ? '' : ' ago'}`;
  } else stat.textContent = 'Saved';
  document.title = S.label ? `${dirty ? '• ' : ''}${S.filename} · Labels` : 'Labels · CoLOS .itf Editor';
}
